import { ReactElement, useEffect, useState } from 'react';
import { useRouter } from 'next/router';
import ProgressBar from '@monofront/design-system/components/ProgressBar';
import { useFirstOrderFormContext } from '@context/firstOrderForm';
import { MultiStepFormActions } from '@monofront/business/stateMachines';
import { useTranslations } from 'next-intl';
import { MultiStepFormStepType } from '@monofront/business/stateMachines/multiStepForm/types';
import { ArrowLeft } from '@monofront/design-system/visuals/Icons/_export';
import { IconSizes } from '@monofront/design-system/visuals/Icons/type';
import { ModalConfirmation } from '@components/ModalConfirmation';

interface ILayoutMultiStepFormProps {
  children?: ReactElement<any>;
}

const LayoutMultiStepForm: React.FC<ILayoutMultiStepFormProps> = ({
  children,
}) => {
  const router = useRouter();
  const { steps, form, state, send, isValidating, currentStep } =
    useFirstOrderFormContext();
  const tAccount = useTranslations('Account');
  const tGlobal = useTranslations('Global');
  const [isOpen, setIsOpen] = useState(false);

  const totalQuestions = Object.values(steps).filter(
    ({ type }) =>
      type === MultiStepFormStepType.MultiSelectCollection ||
      type === MultiStepFormStepType.AddressForm,
  );
  const currentStepIndex = totalQuestions.findIndex(
    ({ uid }) => uid === state.value,
  );
  const completion = ((currentStepIndex + 1) / totalQuestions.length) * 100;

  const {
    getValues,
    formState: { errors },
  } = form;

  const handleGoNextStep = () => {
    const values = Object.values(getValues())[0];

    switch (currentStep.type) {
      case MultiStepFormStepType.MultiSelectCollection:
        send({
          type: MultiStepFormActions.NEXT,
          payload: {
            stepId: state.value,
            collections: values ?? [],
          },
        });

        return;
      case MultiStepFormStepType.AddressForm:
        send({
          type: MultiStepFormActions.NEXT,
          payload: {
            stepId: state.value,
            ...values,
          },
        });

        return;
      default:
        send({
          type: MultiStepFormActions.NEXT,
          payload: {
            stepId: state.value,
          },
        });

        return;
    }
  };

  const handleGoPreviousStep = () => {
    const values = Object.values(getValues())[0];

    send({
      type: MultiStepFormActions.PREVIOUS,
      payload: {
        stepId: state.value,
        collections: state.context.savedCollections ?? values ?? [],
      },
    });
  };

  const handleExitForm = () => {
    send({ type: MultiStepFormActions.EXIT_FORM });
  };

  useEffect(() => {
    router.beforePopState((newState) => {
      // keep current url
      history.pushState(newState, '', router.asPath);
      setIsOpen(true);

      return false;
    });
  }, [router]);

  return (
    <>
      <header className="tw-my-2 tw-flex tw-h-8 tw-w-full tw-flex-row tw-items-center tw-justify-center desktop:tw-my-0 desktop:tw-h-[110px] desktop:tw-flex-col">
        <button
          data-testid="multi-step-form-previous"
          className="tw-lf-button-clear tw-absolute tw-left-0 tw-ml-2 tw-flex desktop:tw-top-[39px] desktop:tw-ml-8"
          onClick={handleGoPreviousStep}
          type="button"
          aria-label={tGlobal('back')}
        >
          <ArrowLeft size={IconSizes.lg} />
        </button>
        <div className="tw-m-4 tw-w-full tw-max-w-[260px] desktop:tw-max-w-[350px]">
          <ProgressBar hasBorder currentProgress={completion} withAnimation />
        </div>
      </header>
      <main className="tw-mx-4 desktop:tw-mx-0">
        <ModalConfirmation
          title={tGlobal('exitFirstOrderFormModalTitle')}
          message={tGlobal('exitFirstOrderFormModalMessage')}
          isOpen={isOpen}
          confirmButtonLabel={tGlobal('exitFirstOrderFormModalConfirmButton')}
          confirmCancelButtonLabel={tGlobal(
            'exitFirstOrderFormModalCancelButton',
          )}
          confirmButtonOnClick={() => {
            handleExitForm();
            setIsOpen(false);
            router.back();
          }}
          cancelButtonOnClick={() => setIsOpen(false)}
          modalClassName="tw-top-[33%]"
        />
        {children}
      </main>
      {currentStep.type !== MultiStepFormStepType.AddressForm ||
      (currentStep.type === MultiStepFormStepType.AddressForm &&
        state.event.type === MultiStepFormActions.SELECT_ADDRESS) ? (
        <footer className="tw-fixed tw-bottom-0 tw-flex tw-h-[68px] tw-w-full tw-flex-row tw-items-center tw-justify-center tw-border-t-[1px] tw-bg-white desktop:tw-mb-0 desktop:tw-h-[76px] desktop:tw-border-solid desktop:tw-border-x-transparent desktop:tw-border-b-transparent desktop:tw-border-t-blue-200">
          <div className="tw-mx-3 tw-flex tw-w-full tw-max-w-[904px] tw-flex-row tw-justify-center desktop:tw-mx-0 desktop:tw-justify-end">
            <button
              className="tw-lf-button tw-lf-button-primary tw-lf-button-md tw-w-full desktop:tw-w-[300px]"
              onClick={handleGoNextStep}
              data-testid="multi-step-form-continue"
              disabled={
                isValidating || !!errors[state.value as keyof typeof steps]
              }
            >
              {currentStep.nextCTAText ||
              state.event.type === MultiStepFormActions.SELECT_ADDRESS
                ? tGlobal('validate')
                : tAccount('continue')}
            </button>
          </div>
        </footer>
      ) : null}
    </>
  );
};

export default LayoutMultiStepForm;
